import { useContext, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router";
import { Button, notification } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { QUERY_EXTRACTION_HISTORY_DATA } from "../queries";
import { ExtractSpecCardItem } from "../model";
import { ProjectPermissionEnum } from "constants/index";

function ExtractionCompletionAlert() {
  const {
    gqlClientForProject,
    isFeatureFlagEnabled,
    tokenContents,
    eventLogs
  } = useContext(ProjectContext);
  const cannotCreateMaterial = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateMaterial,
    tokenContents?.role ?? ""
  );

  const isMaterialExtractionEnabled =
    isFeatureFlagEnabled("MATERIAL_EXTRACTION") && !cannotCreateMaterial;

  const { projectId } = useParams() as { projectId: string };
  const history = useHistory();

  const prevInProgressRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);

  const { data: extractionHistoryData, refetch: refetchExtractionHistoryData } =
    useCIQQuery<{
      extraction_data_progress_func: Array<{
        projectId: string;
        status_wise_extraction_data: Array<ExtractSpecCardItem>;
      }>;
    }>(QUERY_EXTRACTION_HISTORY_DATA, {
      client: gqlClientForProject,
      skip: !gqlClientForProject || !isMaterialExtractionEnabled
    });

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (eventLogs.some((log) => log.data_source === "specifications")) {
        refetchExtractionHistoryData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetchExtractionHistoryData]);

  useEffect(() => {
    const list =
      extractionHistoryData?.extraction_data_progress_func?.[0]
        ?.status_wise_extraction_data || [];

    if (!list) return;

    const currentInProgress = new Set(
      list
        .filter((item) => item.status === "IN_PROGRESS")
        .map((item) => item.specification_id)
    );

    const completedMap = new Map(
      list
        .filter((item) => item.status === "COMPLETED")
        .map((item) => [item.specification_id, item] as const)
    );

    const failedMap = new Map(
      list
        .filter((item) => item.status === "FAILED")
        .map((item) => [item.specification_id, item] as const)
    );

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      prevInProgressRef.current = currentInProgress;
      return;
    }

    prevInProgressRef.current.forEach((specId) => {
      const completedItem = completedMap.get(specId);
      if (completedItem) {
        const key = `extraction-complete-${specId}`;
        notification.open({
          key,
          icon: <CheckCircleFilled className="text-xl text-ciq-success-text" />,
          title: (
            <span className="font-semibold text-base">
              Material Extraction complete
            </span>
          ),
          description: (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                {completedItem.total_materials} Materials extracted from{" "}
                {completedItem.total_schedule_tables} Schedules. Add them to the
                Log to track.
              </p>
              <div className="flex justify-end items-center gap-2">
                <Button type="text" onClick={() => notification.destroy(key)}>
                  Dismiss
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    notification.destroy(key);
                    history.push(`/project/${projectId}/materials/extracted`);
                  }}
                >
                  View
                </Button>
              </div>
            </div>
          ),
          placement: "topRight",
          duration: 0,
          closeIcon: <span className="text-gray-400 text-sm">&#x2715;</span>
        });
      }

      const failedItem = failedMap.get(specId);
      if (failedItem) {
        const key = `extraction-failed-${specId}`;
        notification.open({
          key,
          icon: <CloseCircleFilled className="text-xl text-red-500" />,
          title: (
            <span className="font-semibold text-base">
              Material Extraction failed
            </span>
          ),
          description: (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                {failedItem.file_name}: Extraction failed. Please try again.
              </p>
              <div className="flex justify-end">
                <Button type="text" onClick={() => notification.destroy(key)}>
                  Dismiss
                </Button>
              </div>
            </div>
          ),
          placement: "topRight",
          duration: 0,
          closeIcon: <span className="text-gray-400 text-sm">&#x2715;</span>
        });
      }
    });

    prevInProgressRef.current = currentInProgress;
  }, [extractionHistoryData, projectId, history]);

  return null;
}

export default ExtractionCompletionAlert;
